import React from "react";
import type {
    AccentColourType,
    ExtraType,
    UpholsteryType,
    WheelType,
} from "../../../../../../../common-deprecated/types/CommonTypes";
import useExtra from "../../../../hooks/shared/extras/useExtra";
import AccentColours from "./AccentColours";
import { findExplicit } from "../../../../../../../common-deprecated/utils";
import Price from "../price/Price";
import * as Styles from "./styles/Extra";
import Icon from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import ExtraImage from "./ExtraImage";
import { BnbLabel, useBnbOptionalLabel } from "../../../../utils/labels";
import Popover from "../../../../../../../common-deprecated/components/popover/aem/Popover";
import * as Type from "../../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import * as Button from "../../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import CarDataConsolidator from "../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import type { BuildAndBuyDispatchType } from "../../../../redux/store";
import { useBnbSelector } from "../../../../redux/store";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_EXTRA_ONLY_PACK_MULTIPLE, MODAL_EXTRA_ONLY_PACK_SINGLE } from "../../../../utils/modalConstants";
import { SrOnly } from "../../../../../../../common-deprecated/styles/v2/toyota/utilities/ScreenreaderOnly";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

type ExtraProps = {
    extra: ExtraType | WheelType | UpholsteryType;
    equipped?: boolean;
    equippedOptionId?: string;
    secondaryAccentColours?: AccentColourType[];
    onSelect?: (optionId?: string) => void;
    onOptionSelect?: (optionId: string) => void;
    optional?: boolean;
    enabled?: boolean;
    handleClickOnlyOnIcon?: boolean;
    allowRemoving?: boolean;
    disableFinanceId?: boolean;
    background?: boolean;
    hidePrice?: boolean;
    isInPack?: boolean;
    isInSelectedPack?: boolean;
    isOnlyInPack?: boolean;
};

const Extra = (props: ExtraProps): JSX.Element => {
    const [setItemLabel, includesDescriptionlabel, accessoryDisclaimer] = useBnbOptionalLabel([
        "setItemTooltipDescription",
        "includesTooltipDescription",
        "accessoryDisclaimer",
    ]);

    const { optional = false, enabled = false, handleClickOnlyOnIcon, allowRemoving, disableFinanceId } = props;
    const { isInPack = false, isInSelectedPack = false, isOnlyInPack = false, background, hidePrice } = props;
    const { extra, equipped = false, equippedOptionId = "", secondaryAccentColours, onSelect, onOptionSelect } = props;

    const hideCheckbox = isOnlyInPack && !isInSelectedPack;

    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const items = useBnbSelector((state) => state.items);

    const extraProps = useExtra(extra, equipped, equippedOptionId, secondaryAccentColours, onSelect, onOptionSelect);
    const {
        renderedOptions,
        renderedSelectedOptionId,
        renderedOptionSelect,
        renderedImage,
        renderedPrice,
        renderedSelect,
        setItems,
        includedItems,
        showAccessoryDisclaimer,
    } = extraProps;

    const renderedAccentColours = renderedOptions.filter((option) => !!option.colour);
    const onClick = (event: React.MouseEvent): void => {
        event.preventDefault();
        if (isOnlyInPack) openExtraOnlyAvailableInPacksModal();
        else if (enabled && optional && renderedSelect) renderedSelect();
    };

    // ----------------------------------------------------------------------
    // Tooltip content
    // ----------------------------------------------------------------------
    const visibleSetItems = (setItems || []).filter((item) => item.showsOnCar);
    const visibleIncludedItems = includedItems?.filter((item) => item.showsOnCar);

    const hideExtraTooltip =
        !extra.description &&
        (visibleSetItems.length === 0 || !setItemLabel) &&
        (visibleIncludedItems?.length === 0 || !includesDescriptionlabel);

    const sets =
        visibleSetItems.length > 0 && setItemLabel ? (
            <>
                {setItemLabel}
                <ul>
                    {visibleSetItems.map((s) => (
                        <li key={s.id}>{s.name}</li>
                    ))}
                </ul>
            </>
        ) : null;

    const included =
        visibleIncludedItems && visibleIncludedItems.length > 0 && includesDescriptionlabel ? (
            <>
                {includesDescriptionlabel}
                <ul>{visibleIncludedItems?.map((item) => <li key={item.id}>{item.name}</li>)}</ul>
            </>
        ) : null;

    const tooltipContent = (
        <>
            <span dangerouslySetInnerHTML={{ __html: extra.description || "" }} />
            {sets}
            {included}
            {showAccessoryDisclaimer && <br />}
            {showAccessoryDisclaimer && accessoryDisclaimer && (
                <span dangerouslySetInnerHTML={{ __html: accessoryDisclaimer }} />
            )}
        </>
    );

    const openExtraOnlyAvailableInPacksModal = (): void => {
        const count = CarDataConsolidator.findPackEquipment(extra as ExtraType, items.packs)!.packs?.length;
        dispatch(
            showModal({
                type: count === 1 ? MODAL_EXTRA_ONLY_PACK_SINGLE : MODAL_EXTRA_ONLY_PACK_MULTIPLE,
                extra: extra as ExtraType,
            }),
        );
    };

    const isPackExtra = isInSelectedPack || isOnlyInPack || isInPack;
    const isSelectable = optional && enabled && !hideCheckbox;

    return (
        <Styles.Wrapper
            background={background}
            selected={equipped}
            isInSelectedPack={isInSelectedPack}
            onClick={enabled && !handleClickOnlyOnIcon && !isInSelectedPack ? onClick : undefined}
            hasClick={(!isPackExtra && isSelectable) || isOnlyInPack}
        >
            <Styles.ImageWrapper>
                <ExtraImage src={renderedImage} altTag={extra.name} />
            </Styles.ImageWrapper>
            <Styles.Header>
                <Styles.HeaderTop>
                    {isPackExtra || isSelectable ? (
                        <Styles.NameButton aria-pressed={equipped}>
                            {extra.name}
                            <SrOnly>
                                (
                                {isInSelectedPack ? (
                                    <BnbLabel label="extraIsIncludedInSelectedPack" />
                                ) : isOnlyInPack ? (
                                    <BnbLabel label="extraIsOnlyAvailableInPack" />
                                ) : (
                                    isInPack && <BnbLabel label="extraIsAvailableInPack" />
                                )}
                                )
                                {isSelectable &&
                                    (equipped ? (
                                        <BnbLabel label="bnbA11yButtonExtraDeselect" />
                                    ) : (
                                        <BnbLabel label="bnbA11yButtonExtraSelect" />
                                    ))}
                            </SrOnly>
                        </Styles.NameButton>
                    ) : (
                        <Styles.Name>{extra.name}</Styles.Name>
                    )}

                    <Styles.Description>
                        {!!tooltipContent && !hideExtraTooltip && (
                            <Popover showOnClick>
                                <Type.Text as="div" headingType="milli" styleType="headline">
                                    {extra.name}
                                </Type.Text>
                                {tooltipContent}
                            </Popover>
                        )}
                    </Styles.Description>
                </Styles.HeaderTop>
                {optional && !hidePrice && !!renderedPrice ? (
                    disableFinanceId ? (
                        <Price price={renderedPrice} />
                    ) : (
                        <Price price={renderedPrice} financeItemId={extra.id} />
                    )
                ) : null}
                {renderedOptions.length > 0 && renderedOptionSelect && (
                    <AccentColours
                        enabled
                        colours={renderedAccentColours.map((accentColour) => accentColour.colour!)}
                        selectColour={(colour) => {
                            renderedOptionSelect(
                                renderedAccentColours.find((accentColour) => accentColour.colour!.id === colour.id)!.id,
                            );
                        }}
                        selectedColour={findExplicit(renderedSelectedOptionId, renderedAccentColours).colour!}
                    />
                )}
            </Styles.Header>
            <Styles.Footer darkVariant={isPackExtra}>
                {isInSelectedPack ? (
                    <BnbLabel label="extraIsIncludedInSelectedPack" />
                ) : isOnlyInPack ? (
                    <>
                        <BnbLabel label="extraIsOnlyAvailableInPack" />
                        <Icon variant="chevronRight" />
                    </>
                ) : (
                    isInPack && <BnbLabel label="extraIsAvailableInPack" />
                )}
                {isSelectable && (
                    <Button.Checkbox
                        selected={equipped}
                        onClick={handleClickOnlyOnIcon ? onClick : undefined}
                        disabled={isInSelectedPack}
                        aria-pressed={equipped}
                    >
                        <SrOnly>
                            {equipped ? (
                                <BnbLabel label="bnbA11yButtonExtraDeselect" />
                            ) : (
                                <BnbLabel label="bnbA11yButtonExtraSelect" />
                            )}
                        </SrOnly>
                        <IconWrapper>
                            {!handleClickOnlyOnIcon ? (
                                equipped ? (
                                    <Icon variant="close" />
                                ) : (
                                    <Icon variant="plus" />
                                )
                            ) : (
                                equipped && allowRemoving && <Icon variant="close" />
                            )}
                        </IconWrapper>
                    </Button.Checkbox>
                )}
            </Styles.Footer>
        </Styles.Wrapper>
    );
};

export default Extra;
